/* eslint-disable no-nested-ternary */
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { useMemo, useState } from 'react';

import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import { useGetArtifactUnpackedQuery } from '@/services/api/artifactService';
import { useGetBlobWithMarkdownContentQuery } from '@/services/api/blobService';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { CitationThumbnail } from './CitationThumbnail';

// Generic interface for evidence that can be displayed
export interface EvidenceData {
  /** ID of the artifact this evidence comes from */
  artifact_id?: string | null;
  /** Citation IDs that support this evidence */
  citation_ids?: string[] | null;
  /** Context or description of where this evidence was found */
  context?: string | null;
  /** When this evidence was created */
  created_at?: string;
  /** Rationale or reasoning for this evidence */
  rationale?: string | null;
  /** Source description */
  extraction_source?: string | null;
  /** Supporting text excerpts */
  excerpts?: string[] | null;
  /** Additional context */
  extraction_context?: string | null;
  /** Confidence notes */
  confidence_notes?: string | null;
  /** Artifact type for inspector functionality */
  artifact_type?: ArtifactType | null;
  /** Confidence score */
  confidence?: number | null;
}

export interface EvidenceCardProps {
  /** The evidence data to display */
  evidence: EvidenceData;
  /** Matter ID for API calls */
  matterId: string;
  /** Optional title override */
  title?: string;
  /** Optional subtitle override */
  subtitle?: string;
  /** Whether to show timestamp */
  showTimestamp?: boolean;
  /** Additional context to display */
  additionalContext?: React.ReactNode;
  /** Visual style variant. Default renders existing rich card; 'minimal' matches Figma */
  variant?: 'default' | 'minimal';
  /** In minimal variant, show the first excerpt as a snippet line */
  showSnippet?: boolean;
}

// Helper function to check if a MIME type represents an image
const isImageMimeType = (mimeType: string | null | undefined): boolean => {
  if (!mimeType) return false;
  return mimeType.startsWith('image/');
};

export const EvidenceCard = ({
  evidence,
  matterId,
  title,
  subtitle,
  showTimestamp = false,
  additionalContext,
  variant = 'default',
  showSnippet = true,
}: EvidenceCardProps) => {
  const [isInspectModalOpen, setIsInspectModalOpen] = useState(false);

  const timeAgo = evidence.created_at
    ? formatDistanceToNow(new Date(evidence.created_at), { addSuffix: true })
    : null;

  // First, get the unpacked artifact data
  const {
    data: unpackedArtifact,
    isLoading: isLoadingArtifact,
    error: artifactError,
  } = useGetArtifactUnpackedQuery(
    {
      matterId,
      artifactId: evidence.artifact_id ?? '',
    },
    {
      skip: !evidence.artifact_id,
    },
  );

  // Extract blob ID from unpacked artifact
  const blobId = unpackedArtifact?.blob?.id;

  // Then fetch blob data to get citation preview URLs
  const {
    data: blobData,
    isLoading: isLoadingBlob,
    error: blobError,
  } = useGetBlobWithMarkdownContentQuery(
    {
      matterId,
      blobId: blobId ?? '',
    },
    {
      skip: !blobId || !evidence.artifact_id,
    },
  );

  // Create citation preview map from block data
  const citationPreviewMap = useMemo(() => {
    if (!blobData?.blocks) return {};
    return blobData.blocks.reduce<Record<string, string>>((acc, block) => {
      acc[block.block_id] = block.preview;
      return acc;
    }, {});
  }, [blobData?.blocks]);

  const hasVisualCitations =
    (evidence.citation_ids?.length || 0) > 0 && evidence.artifact_id;

  // Check if the blob itself is an image but has no citation IDs
  const isBlobImage =
    evidence.artifact_id &&
    (evidence.citation_ids?.length || 0) === 0 &&
    unpackedArtifact?.blob &&
    isImageMimeType(unpackedArtifact.blob.file_mime_type);

  const isLoading = isLoadingArtifact || isLoadingBlob;
  const error = artifactError || blobError;

  // Check if this is a staff note to display content directly
  const isStaffNote = unpackedArtifact?.artifact_type === 'STAFF_NOTE';
  const staffNoteContent = isStaffNote
    ? unpackedArtifact?.staff_note?.content
    : null;

  // Get a clean document name from the blob or artifact
  const documentName =
    title ||
    unpackedArtifact?.blob?.name ||
    evidence.extraction_source ||
    (isStaffNote
      ? 'Staff Note'
      : evidence.artifact_id
        ? `Document ${evidence.artifact_id.slice(-8)}`
        : 'Unknown Document');

  const canOpenInspector =
    evidence.artifact_id && unpackedArtifact?.artifact_type;

  // Streamlined presentation - clean, focused, minimal boxes
  const { confidence } = evidence;
  const confidenceClass =
    confidence === null || confidence === undefined
      ? 'bg-gray-100 text-gray-700'
      : confidence > 0.8
        ? 'bg-green-100 text-green-800'
        : confidence > 0.6
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-red-100 text-red-800';

  if (variant === 'minimal') {
    return (
      <div className="space-y-2 text-sm">
        {/* Compact header: doc + confidence inline */}
        <div className="flex items-baseline gap-2">
          {canOpenInspector ? (
            <button
              type="button"
              className="text-sm font-medium text-gray-900 hover:text-blue-600 underline decoration-gray-300 decoration-dotted underline-offset-2 hover:decoration-blue-500"
              onClick={() => setIsInspectModalOpen(true)}
              title={documentName}
            >
              {documentName}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {documentName}
            </span>
          )}
          {confidence !== null && confidence !== undefined && (
            <span
              className={`text-xs font-medium ${
                confidence > 0.8
                  ? 'text-green-700'
                  : confidence > 0.6
                    ? 'text-yellow-700'
                    : 'text-red-700'
              }`}
            >
              {(confidence * 100).toFixed(0)}%
            </span>
          )}
        </div>

        {/* Narrative - plain paragraph */}
        {evidence.rationale && (
          <p className="text-sm leading-relaxed text-gray-700">
            {evidence.rationale}
          </p>
        )}

        {/* Single excerpt - minimal */}
        {showSnippet && evidence.excerpts && evidence.excerpts.length > 0 && (
          <p className="text-xs italic text-gray-500 pl-3 border-l border-gray-300">
            {evidence.excerpts[0]}
          </p>
        )}

        {/* Thumbnails - minimal stack, no container */}
        {hasVisualCitations && !isLoading && !error && (
          <div className="space-y-1.5 pt-1">
            {evidence.citation_ids
              ?.slice(0, 2)
              .map((citationId) => (
                <CitationThumbnail
                  key={citationId}
                  citationId={citationId}
                  previewUrl={citationPreviewMap[citationId]}
                  isFullWidth={false}
                />
              ))}
            {(evidence.citation_ids?.length || 0) > 2 && (
              <div className="text-xs text-gray-400">
                +{(evidence.citation_ids?.length || 0) - 2} more
              </div>
            )}
          </div>
        )}

        {isBlobImage && !isLoading && !error && (
          <div className="pt-1">
            <CitationThumbnail
              blob={unpackedArtifact.blob}
              isFullWidth={false}
            />
          </div>
        )}

        {canOpenInspector && (
          <InspectArtifactModal
            artifactId={evidence.artifact_id ?? ''}
            isOpen={isInspectModalOpen}
            onOpenChange={setIsInspectModalOpen}
          />
        )}
      </div>
    );
  }

  // Default: ultra-streamlined
  return (
    <div className="py-3 space-y-2.5 text-sm">
      {/* Inline header */}
      <div className="flex items-baseline gap-2 flex-wrap">
        {canOpenInspector ? (
          <button
            type="button"
            className="font-medium text-gray-900 hover:text-blue-600 underline decoration-gray-300 decoration-dotted underline-offset-2"
            onClick={() => setIsInspectModalOpen(true)}
            title={documentName}
          >
            {documentName}
          </button>
        ) : (
          <span className="font-medium text-gray-900">{documentName}</span>
        )}

        {confidence !== null && confidence !== undefined && (
          <span
            className={`text-xs font-medium ${
              confidence > 0.8
                ? 'text-green-700'
                : confidence > 0.6
                  ? 'text-yellow-700'
                  : 'text-red-700'
            }`}
          >
            {(confidence * 100).toFixed(0)}%
          </span>
        )}

        {subtitle && (
          <span className="text-xs text-gray-500">· {subtitle}</span>
        )}
        {showTimestamp && timeAgo && (
          <span className="text-xs text-gray-500">· {timeAgo}</span>
        )}
      </div>

      {/* Plain narrative */}
      {evidence.rationale && (
        <p className="text-sm leading-relaxed text-gray-700">
          {evidence.rationale}
        </p>
      )}

      {/* Max 1 excerpt - minimal */}
      {evidence.excerpts && evidence.excerpts.length > 0 && (
        <>
          <p className="text-xs italic text-gray-500 pl-3 border-l border-gray-300">
            {evidence.excerpts[0]}
          </p>
          {evidence.excerpts.length > 1 && (
            <div className="text-xs text-gray-400 pl-3">
              +{evidence.excerpts.length - 1} more
            </div>
          )}
        </>
      )}

      {additionalContext}

      {/* Staff Note Content - Display note text with author and timestamp */}
      {isStaffNote && staffNoteContent && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <PencilSquareIcon className="h-4 w-4 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs font-medium text-amber-800">
                  Staff Note
                </div>
                {unpackedArtifact?.staff_note?.user && (
                  <>
                    <span className="text-amber-600">•</span>
                    <span className="text-xs text-amber-700 font-medium">
                      {unpackedArtifact.staff_note.user.first_name}{' '}
                      {unpackedArtifact.staff_note.user.last_name}
                    </span>
                  </>
                )}
                {unpackedArtifact?.staff_note?.created_at && (
                  <>
                    <span className="text-amber-600">•</span>
                    <span className="text-xs text-amber-600">
                      {formatDistanceToNow(
                        new Date(unpackedArtifact.staff_note.created_at),
                        { addSuffix: true },
                      )}
                    </span>
                  </>
                )}
              </div>
              <div className="text-sm text-amber-900 whitespace-pre-wrap">
                {staffNoteContent}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnails - minimal, max 2 */}
      {hasVisualCitations && !isLoading && !error && (
        <div className="space-y-1.5">
          {evidence.citation_ids
            ?.slice(0, 2)
            .map((citationId) => (
              <CitationThumbnail
                key={citationId}
                citationId={citationId}
                previewUrl={citationPreviewMap[citationId]}
                isFullWidth={false}
              />
            ))}
          {(evidence.citation_ids?.length || 0) > 2 && (
            <div className="text-xs text-gray-400">
              +{(evidence.citation_ids?.length || 0) - 2} more
            </div>
          )}
        </div>
      )}

      {isBlobImage && !isLoading && !error && (
        <CitationThumbnail blob={unpackedArtifact.blob} isFullWidth={false} />
      )}

      {/* Non-visual artifacts note */}
      {(evidence.citation_ids?.length || 0) > 0 && !evidence.artifact_id && (
        <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-100">
          📄 {evidence.citation_ids?.length || 0} reference
          {(evidence.citation_ids?.length || 0) > 1 ? 's' : ''} in this document
        </div>
      )}

      {/* Modal for artifact inspection */}
      {canOpenInspector && (
        <InspectArtifactModal
          artifactId={evidence.artifact_id ?? ''}
          isOpen={isInspectModalOpen}
          onOpenChange={setIsInspectModalOpen}
        />
      )}
    </div>
  );
};
